import { useCallback, useMemo } from 'react';
import { InsuranceDetail, VisaDetail } from '../types/PackageBuilder';
import { ItineraryItem, TripInfo } from '../types/Quotation';
import { PackageCabPricingRow } from '../types/TravelPackage';
import { getDatePlusDays } from '../utils/date';
import { calculateQuotationTransfer } from '../utils/packageTemplate';
import { buildAutoSlug } from '../utils/slug';
import { countWords } from '../utils/text';
import { BreakdownCategoryTotals } from '../components/PackageBuilder/contracts';

type FaqLimits = {
  questionMax: number;
  answerMaxWords: number;
};

type Addon = {
  name: string;
  price: number;
  currency: string;
  description: string;
  quoteBasePrice?: number;
};

type PriceOverrides = {
  flights: number | null;
  hotels: number | null;
  transfers: number | null;
  visa: number | null;
  insurance: number | null;
  activities: number | null;
  addons: number | null;
};

type UsePackageBuilderDerivedArgs = {
  builderMode: 'package' | 'quotation';
  tripInfo: TripInfo;
  itinerary: ItineraryItem[];
  nightsCount: number;
  addons: Addon[];
  insuranceDetails: InsuranceDetail[];
  visaDetails: VisaDetail[];
  priceOverrides: PriceOverrides;
  markupType: 'percent' | 'fixed';
  markupValue: number;
  currency: 'INR' | 'USD';
  hotelCategory: string;
  importantTitleDraft: string;
  importantDescriptionDraft: string;
  faqLimits: FaqLimits;
  persistedBreakdown: BreakdownCategoryTotals | null;
  initialLiveTotals: BreakdownCategoryTotals | null;
  quotationPackageId?: string;
  quotationTransferRows?: PackageCabPricingRow[];
  quotationTransferOverride?: number | null;
  quotationFinalOverride?: number | null;
  quotationTransferSelection?: {
    sourceRowId?: string;
    cabType?: string;
  } | null;
};

export const usePackageBuilderDerived = ({
  builderMode,
  tripInfo,
  itinerary,
  nightsCount,
  addons,
  insuranceDetails,
  visaDetails,
  priceOverrides,
  markupType,
  markupValue,
  currency,
  hotelCategory,
  importantTitleDraft,
  importantDescriptionDraft,
  faqLimits,
  persistedBreakdown,
  initialLiveTotals,
  quotationPackageId,
  quotationTransferRows = [],
  quotationTransferOverride,
  quotationFinalOverride,
  quotationTransferSelection,
}: UsePackageBuilderDerivedArgs) => {
  const resolveOverride = (base: number, override: number | null) => {
    if (override === null || override === undefined || Number.isNaN(override)) return base;
    return Math.max(0, override);
  };

  const cities = useMemo(
    () => tripInfo.places.map((p) => p.label || p.value).filter(Boolean),
    [tripInfo.places]
  );
  const slug = useMemo(
    () => buildAutoSlug(tripInfo.title, nightsCount, cities),
    [tripInfo.title, nightsCount, cities]
  );
  const descriptionWordCount = useMemo(
    () => countWords(tripInfo.tripOverview || ''),
    [tripInfo.tripOverview]
  );
  const totalPax = useMemo(() => {
    const total = Number(tripInfo.travellers?.adults || 0)
      + Number(tripInfo.travellers?.children || 0)
      + Number(tripInfo.travellers?.infants || 0);
    return Math.max(1, total);
  }, [tripInfo.travellers]);
  const activityTotalPax = useMemo(() => ({
    adults: Math.max(0, Number(tripInfo.travellers?.adults || 0)),
    children: Math.max(0, Number(tripInfo.travellers?.children || 0)),
  }), [tripInfo.travellers]);
  const addonsTotal = useMemo(
    () => addons.reduce((sum, addon) => sum + (addon.price || 0), 0),
    [addons]
  );
  const rawAddonsTotalForPackage = useMemo(() => {
    if (builderMode !== 'package') return addonsTotal;
    return addons
      .filter((addon) => (addon?.name || '').toLowerCase() !== 'insurance')
      .reduce((sum, addon) => sum + (addon.price || 0), 0);
  }, [addons, addonsTotal, builderMode]);
  const rawInsuranceTotal = useMemo(
    () => insuranceDetails.reduce((sum, item) => sum + (Number(item.price || 0) || 0), 0) * totalPax,
    [insuranceDetails, totalPax]
  );
  const rawVisaTotal = useMemo(
    () => visaDetails.reduce((sum, visa) => sum + (Number(visa.price || 0) || 0), 0) * totalPax,
    [visaDetails, totalPax]
  );
  const rawFlightsTotal = useMemo(() => (
    itinerary.reduce(
      (sum, day) => sum + day.flights.reduce((daySum, flight) => daySum + (flight.price || 0), 0),
      0
    )
  ), [itinerary]);
  const rawHotelsTotal = useMemo(() => (
    itinerary.reduce(
      (sum, day) => sum + day.hotels.reduce((daySum, hotel) => daySum + (hotel.price || 0), 0),
      0
    )
  ), [itinerary]);
  const rawActivitiesTotal = useMemo(() => (
    itinerary.reduce((sum, day) => sum + day.activities.reduce((daySum, activity: any) => {
      if (activity.inclusionType === 'optional') return daySum;
      const quotedTotal = Number(activity.quoted_total);
      if (Number.isFinite(quotedTotal) && quotedTotal >= 0) return daySum + quotedTotal;
      const fallback = Number(activity.from_price_paise || 0);
      const adult = Number(activity.adult_price_paise ?? fallback);
      const child = Number(activity.child_price_paise ?? fallback);
      const infant = Number(activity.infant_price_paise || 0);
      const cab = Number(activity.cab_total || 0);
      return daySum
        + Math.round(adult / 100) * activityTotalPax.adults
        + Math.round(child / 100) * activityTotalPax.children
        + Math.round(infant / 100) * Math.max(0, Number(tripInfo.travellers?.infants || 0))
        + cab;
    }, 0), 0)
  ), [activityTotalPax, itinerary, tripInfo.travellers?.infants]);
  const rawTransfersTotal = useMemo(() => (
    itinerary.reduce(
      (sum, day) => sum + (day.transfers || []).reduce((daySum, transfer) => daySum + (transfer.price || 0), 0),
      0
    )
  ), [itinerary]);
  const applyPersistedDelta = useCallback((key: keyof BreakdownCategoryTotals, rawValue: number) => {
    if (!persistedBreakdown || !initialLiveTotals) return rawValue;
    const baseValue = Number(persistedBreakdown[key] || 0);
    const initialValue = Number(initialLiveTotals[key] || 0);
    return Math.max(0, baseValue + (rawValue - initialValue));
  }, [initialLiveTotals, persistedBreakdown]);
  const calculatedFlightsTotal = useMemo(() => applyPersistedDelta('flights', rawFlightsTotal), [applyPersistedDelta, rawFlightsTotal]);
  const calculatedHotelsTotal = useMemo(() => applyPersistedDelta('hotels', rawHotelsTotal), [applyPersistedDelta, rawHotelsTotal]);
  const calculatedTransfersTotal = useMemo(() => applyPersistedDelta('transfers', rawTransfersTotal), [applyPersistedDelta, rawTransfersTotal]);
  const calculatedVisaTotal = useMemo(() => applyPersistedDelta('visa', rawVisaTotal), [applyPersistedDelta, rawVisaTotal]);
  const calculatedInsuranceTotal = useMemo(() => applyPersistedDelta('insurance', rawInsuranceTotal), [applyPersistedDelta, rawInsuranceTotal]);
  const calculatedActivitiesTotal = useMemo(() => applyPersistedDelta('activities', rawActivitiesTotal), [applyPersistedDelta, rawActivitiesTotal]);
  const calculatedAddonsTotalForPackage = useMemo(() => applyPersistedDelta('addons', rawAddonsTotalForPackage), [applyPersistedDelta, rawAddonsTotalForPackage]);
  const addonsTotalForPackage = resolveOverride(calculatedAddonsTotalForPackage, priceOverrides.addons);
  const flightsTotal = resolveOverride(calculatedFlightsTotal, priceOverrides.flights);
  const hotelsTotal = resolveOverride(calculatedHotelsTotal, priceOverrides.hotels);
  const quotationTransferCalculation = useMemo(() => {
    if (builderMode !== 'quotation') return null;
    return calculateQuotationTransfer({
      packageId: quotationPackageId,
      rows: quotationTransferRows,
      pax: totalPax,
      tripStartDate: tripInfo.startDate,
      overrideAmount: quotationTransferOverride,
      fallbackAmount: calculatedTransfersTotal,
      preferredSourceRowId: quotationTransferSelection?.sourceRowId,
      preferredCabType: quotationTransferSelection?.cabType,
    });
  }, [
    builderMode,
    calculatedTransfersTotal,
    quotationPackageId,
    quotationTransferOverride,
    quotationTransferRows,
    quotationTransferSelection?.cabType,
    quotationTransferSelection?.sourceRowId,
    totalPax,
    tripInfo.startDate,
  ]);
  const transfersTotal = builderMode === 'quotation' && quotationTransferCalculation
    ? quotationTransferCalculation.total
    : resolveOverride(calculatedTransfersTotal, priceOverrides.transfers);
  const visaTotal = resolveOverride(calculatedVisaTotal, priceOverrides.visa);
  const insuranceTotal = resolveOverride(calculatedInsuranceTotal, priceOverrides.insurance);
  const activitiesTotal = resolveOverride(calculatedActivitiesTotal, priceOverrides.activities);
  const inclusionsTotal = useMemo(
    () => flightsTotal + hotelsTotal + activitiesTotal + transfersTotal + addonsTotalForPackage + visaTotal + insuranceTotal,
    [flightsTotal, hotelsTotal, activitiesTotal, transfersTotal, addonsTotalForPackage, visaTotal, insuranceTotal]
  );
  const markupAmount = useMemo(() => {
    const base = Number(inclusionsTotal || 0);
    const val = Number(markupValue || 0);
    if (markupType === 'percent') {
      return Math.round((base * val) / 100);
    }
    return val;
  }, [inclusionsTotal, markupValue, markupType]);
  const finalTotal = useMemo(() => inclusionsTotal + markupAmount, [inclusionsTotal, markupAmount]);
  const displayedTotal = useMemo(() => {
    if (builderMode !== 'quotation') return finalTotal;
    if (quotationFinalOverride === null || quotationFinalOverride === undefined || Number.isNaN(quotationFinalOverride)) {
      return finalTotal;
    }
    return Math.max(0, quotationFinalOverride);
  }, [builderMode, finalTotal, quotationFinalOverride]);
  const currencySymbol = currency === 'USD' ? '$' : '₹';
  const importantTitleCount = useMemo(() => importantTitleDraft.trim().length, [importantTitleDraft]);
  const importantAnswerWordCount = useMemo(
    () => countWords(importantDescriptionDraft || ''),
    [importantDescriptionDraft]
  );
  const canAddImportantInfo = importantTitleCount > 0
    && importantTitleCount <= faqLimits.questionMax
    && importantAnswerWordCount > 0
    && importantAnswerWordCount <= faqLimits.answerMaxWords;
  const defaultHotelStartDate = useMemo(() => getDatePlusDays(new Date().toISOString(), 30), []);
  const hotelMinStars = useMemo(() => {
    const raw = (hotelCategory || '').trim();
    if (!raw) return 0;
    const match = raw.match(/\d/);
    if (match) {
      const val = Number(match[0]);
      return Number.isFinite(val) ? val : 0;
    }
    const lower = raw.toLowerCase();
    if (lower.includes('luxury')) return 5;
    if (lower.includes('budget')) return 2;
    return 0;
  }, [hotelCategory]);
  return {
    cities,
    slug,
    descriptionWordCount,
    addonsTotal,
    rawFlightsTotal,
    rawHotelsTotal,
    rawTransfersTotal,
    rawVisaTotal,
    rawInsuranceTotal,
    rawActivitiesTotal,
    rawAddonsTotalForPackage,
    addonsTotalForPackage,
    calculatedFlightsTotal,
    calculatedHotelsTotal,
    calculatedTransfersTotal,
    calculatedVisaTotal,
    calculatedInsuranceTotal,
    calculatedActivitiesTotal,
    calculatedAddonsTotalForPackage,
    insuranceTotal,
    visaTotal,
    flightsTotal,
    hotelsTotal,
    activitiesTotal,
    transfersTotal,
    inclusionsTotal,
    markupAmount,
    finalTotal,
    displayedTotal,
    currencySymbol,
    importantTitleCount,
    importantAnswerWordCount,
    canAddImportantInfo,
    defaultHotelStartDate,
    totalPax,
    hotelMinStars,
    quotationTransferCalculation,
  };
};
